import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// mantine
import { Text, Box, Divider } from '@mantine/core';
import { semanticColors } from 'theme';

// tabler icons
import {
  IconDashboard,
  IconUserCircle,
  IconBookmarks,
  IconBellRinging,
  // IconPlane, // restore with the Flight Deal Scanner menu item below
  IconAward,
  IconWorldSearch,
  IconCompass,
  IconCamera,
  IconCreditCard,
  IconUserPlus,
  IconLogout,
  IconCurrencyDollar,
  IconChartLine,
  IconTrendingUp,
  IconBuildingStore,
  IconShieldCheck,
  IconChartBar,
  IconBroadcast
} from '@tabler/icons-react';

// project imports
import NavGroup from './NavGroup';
import useUserPrefix from 'hooks/use-user-prefix';
import { pointsScannerEnabled } from 'utils/constants';

// ==============================|| SIDEBAR MENU LIST ||============================== //

const MenuList = ({ drawerOpen }) => {
  const userData = useSelector((state) => state.user?.data);
  const prefix = useUserPrefix();

  const userType = userData?.userType || 'member';
  const cannotUseAppFeats = userType === 'member' && !userData.isOTPVerified;

  const appMenuOptions = [
    {
      id: 'apps',
      title: 'My Apps',
      type: 'item',
      url: '/my-apps',
      icon: IconDashboard,
      disabled: false
    },
    {
      id: 'profile',
      title: 'My Profile',
      type: 'item',
      url: '/profile',
      icon: IconUserCircle
    },
    {
      id: 'collection',
      title: 'My Collection',
      type: 'item',
      url: '/collection',
      icon: IconBookmarks,
      disabled: cannotUseAppFeats
    },
    {
      id: 'notification-settings',
      title: 'Notification Settings',
      type: 'item',
      url: '/notification-settings',
      icon: IconBellRinging,
      disabled: cannotUseAppFeats
    },
    // TEMPORARILY HIDDEN: Flight Deal Scanner is not functional yet. Restore when fixed.
    // {
    //   id: 'flight-deal-scanner',
    //   title: 'Flight Deal Scanner',
    //   type: 'item',
    //   url: '/flight-deal-scanner',
    //   icon: IconPlane,
    //   breadcrumbs: false,
    //   disabled: cannotUseAppFeats
    // },
    ...(pointsScannerEnabled
      ? [
          {
            id: 'points-scanner',
            title: 'Points Deal Scanner',
            type: 'item',
            url: '/points',
            icon: IconAward,
            breadcrumbs: false,
            disabled: cannotUseAppFeats
          }
        ]
      : []),
    {
      id: 'trip-planner-ai',
      title: 'Trip Planner AI',
      type: 'item',
      url: '/ai-trip-plans',
      icon: IconWorldSearch,
      disabled: cannotUseAppFeats
    },
    {
      id: 'trip-guides',
      title: 'Trip Guide Studio',
      type: 'item',
      url: '/trip-guide-studio',
      icon: IconCompass,
      disabled: cannotUseAppFeats
    },
    {
      id: 'live-stream',
      title: 'Travel Hub',
      type: 'item',
      url: '/stream',
      icon: IconCamera,
      disabled: cannotUseAppFeats
    }
  ];

  const userMenuOptions = [
    {
      id: 'subscription',
      title: 'My Subscription',
      type: 'item',
      url: '/subscription',
      icon: IconCreditCard
    },
    {
      id: 'referrals',
      title: 'Refer a Friend',
      type: 'item',
      url: '/referral',
      icon: IconUserPlus
    },
    {
      id: 'logout',
      title: 'Log Out',
      type: 'item',
      url: '/',
      icon: IconLogout
    }
  ];

  const influencerMenuOptions = [
    {
      id: 'sales-dashboard',
      title: 'Sales',
      type: 'item',
      url: `/${prefix}/sales-dashboard`,
      icon: IconCurrencyDollar
    },
    {
      id: 'influencer',
      title: 'My Referrals',
      type: 'item',
      url: `/${prefix}/referrals`,
      icon: IconChartLine
    },
    {
      id: 'analytics',
      title: 'Analytics',
      type: 'item',
      url: `/${prefix}/analytics`,
      icon: IconTrendingUp
    },
    {
      id: 'invite-user',
      title: 'Add Referrals',
      type: 'item',
      url: `/${prefix}/invite-a-user`,
      icon: IconUserPlus
    },
    {
      id: 'live-stream',
      title: 'Spotlight',
      type: 'item',
      url: `/${prefix}/spotlight`,
      icon: IconBroadcast,
      disabled: cannotUseAppFeats
    }
  ];

  const stripeMenuOptions = [
    {
      id: 'stripe',
      title: 'Stripe Connect',
      type: 'item',
      url: `/${prefix}/stripe-connect-onboarding`,
      icon: IconBuildingStore
    },
    {
      id: 'balances',
      title: 'Stripe Connect Balances',
      type: 'item',
      url: `/${prefix}/stripe-connect/balances`,
      icon: IconBuildingStore
    }
  ];

  const superAdminOptions = [
    {
      id: 'super-admin-panel',
      title: 'Admin Panel',
      type: 'item',
      url: 'admin/super-admin',
      icon: IconShieldCheck
    },
    {
      id: 'user-analytics',
      title: 'User Analytics',
      type: 'item',
      url: 'admin/user-analytics',
      icon: IconChartBar
    },
    {
      id: 'influencer-analytics',
      title: 'Influencer Analytics',
      type: 'item',
      url: 'admin/influencer-analytics',
      icon: IconChartLine
    },
    {
      id: 'influencer-analytics',
      title: 'Creator Analytics',
      type: 'item',
      url: 'admin/creator-analytics',
      icon: IconChartLine
    }
  ];

  const appMenuItems = [
    {
      id: 'app-features',
      type: 'group',
      children: [...appMenuOptions]
    }
  ];

  const basicMenuItems = [
    {
      id: 'core-features',
      type: 'group',
      children: [...userMenuOptions]
    }
  ];

  /**
   *  Influencer menu items
   */

  const influencerStripeMenuItems = [
    {
      id: 'stripe-features',
      type: 'group',
      children: userData.isApproved ? [...stripeMenuOptions] : [...stripeMenuOptions.filter((item) => item.id !== 'balances')]
    }
  ];

  const influencerMenuItems = [
    {
      id: 'influencer',
      type: 'group',
      children: [...influencerMenuOptions]
    }
  ];

  const superAdminMenuItems = [
    {
      id: 'super-admin',
      type: 'group',
      children: [...superAdminOptions]
    }
  ];

  const logoutMenuItem = [
    {
      id: 'log-out',
      type: 'group',
      children: [
        {
          id: 'logout',
          title: 'Log Out',
          type: 'item',
          url: '/',
          icon: IconLogout
        }
      ]
    }
  ];

  const menuItemsMappedByUserType = {
    user: [...appMenuItems, ...basicMenuItems],
    member: [...appMenuItems, ...basicMenuItems],
    influencer: userData.isApproved
      ? [...influencerMenuItems, ...influencerStripeMenuItems, ...appMenuItems, ...logoutMenuItem]
      : [...influencerStripeMenuItems, ...logoutMenuItem],
    creator: userData.isApproved
      ? [...influencerMenuItems, ...influencerStripeMenuItems, ...appMenuItems, ...logoutMenuItem]
      : [...influencerStripeMenuItems, ...logoutMenuItem],
    admin: [...superAdminMenuItems, ...appMenuItems, ...logoutMenuItem]
  };

  const navItems = menuItemsMappedByUserType[userType].map((item) => {
    switch (item.type) {
      case 'group':
        return <NavGroup key={item.id} item={item} opened={drawerOpen} />;
      default:
        return (
          <Text key={item.id} c="red" ta="center" size="sm">
            Menu Items Error
          </Text>
        );
    }
  });

  return (
    <Box>
      <Divider my="md" color={semanticColors.border.default} />
      {navItems}
    </Box>
  );
};

MenuList.propTypes = {
  drawerOpen: PropTypes.bool
};

export default MenuList;
